import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import RotatingText from './RotatingText';
import { ArrowRight, Sparkles, Play, ShieldCheck } from 'lucide-react';

const HeroSection = ({ sliders = [] }) => {
    const navigate = useNavigate();
    const { scrollY } = useScroll();

    // Parallax effect for the background elements
    const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
    const y2 = useTransform(scrollY, [0, 1000], [0, -100]);
    const opacity = useTransform(scrollY, [0, 500], [1, 0]);

    const currentSlide = useState(0)[0];
    const setCurrentSlide = useState(0)[1];


    useEffect(() => {
        if (sliders.length > 1) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % sliders.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [sliders]);

    const rotatingTexts = [
        'Software Solutions',
        'Transformation',
        'Cloud Architecture',
        'AI & ML Integration',
        'Bespoke Web Apps',
        'Mobile Experiences',
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20, filter: 'blur(10px)' },
        visible: {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            transition: { type: 'spring', stiffness: 100, damping: 20 }
        }
    };

    const heroImage = sliders[currentSlide]?.image_sliders_home || "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070&auto=format&fit=crop";

    return (
        <div className="relative min-h-[90vh] lg:min-h-screen flex flex-col lg:flex-row items-center overflow-hidden bg-background pt-24 lg:pt-0">

            {/* Interactive Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808030_1px,transparent_1px),linear-gradient(to_bottom,#80808030_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>


            {/* Static accent gradients */}
            <motion.div style={{ y: y1 }} className="absolute -top-[10%] -left-[5%] w-[40%] h-[40%] rounded-full bg-primary/10 blur-[100px] pointer-events-none" />
            <motion.div style={{ y: y2 }} className="absolute bottom-[10%] -right-[5%] w-[40%] h-[40%] rounded-full bg-accent/10 blur-[100px] pointer-events-none" />

            <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">

                    <motion.div
                        style={{ opacity }}
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="flex flex-col items-start text-left order-1 lg:col-start-1"
                    >
                        <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl md:text-6xl font-black font-heading tracking-tight text-foreground leading-[1.05] mb-6">
                            Architecting
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Professional</span>
                            <div className="h-[50px] sm:h-[60px] md:h-[70px] overflow-hidden mt-1 text-foreground">
                                <RotatingText
                                    texts={rotatingTexts}
                                    mainClassName="text-foreground font-heading"
                                    staggerFrom="last"
                                    initial={{ y: "100%" }}
                                    animate={{ y: 0 }}
                                    exit={{ y: "-120%" }}
                                    staggerDuration={0.02}
                                    splitLevelClassName="overflow-hidden"
                                    transition={{ type: "spring", damping: 30, stiffness: 400 }}
                                    rotationInterval={3000}
                                />
                            </div>
                        </motion.h1>

                        <motion.p variants={itemVariants} className="md:text-lg text-sm text-muted-foreground max-w-xl font-body leading-relaxed mb-8 md:mb-10 border-l-2 border-primary/30 pl-6">
                            We transform complex enterprise hurdles into elegant, high-performance software architectures. Experience the future of engineering excellence today.
                        </motion.p>

                        {/* Buttons - Single row on mobile, unchanged on desktop */}
                        <motion.div variants={itemVariants} className="flex flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                            <motion.button
                                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(var(--color-primary), 0.4)" }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/contact')}
                                className="flex-1 sm:flex-initial px-4 sm:px-8 py-3 sm:py-4 bg-primary text-primary-foreground font-bold rounded-full shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group text-sm sm:text-base"
                            >
                                Get Started
                                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.05, backgroundColor: "rgba(var(--color-foreground), 0.1)" }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/services')}
                                className="flex-1 sm:flex-initial px-4 sm:px-8 py-3 sm:py-4 bg-foreground/5 dark:bg-foreground/10 border border-border text-foreground font-bold rounded-full backdrop-blur-sm transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <Play className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                                Our Services
                            </motion.button>
                        </motion.div>
                    </motion.div>

                    {/* Right Column: Video Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, rotateY: 10 }}
                        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                        transition={{ duration: 1, delay: 0.3, type: "spring" }}
                        className="relative perspective-1000 flex justify-center items-center -mt-2 lg:-mt-0 order-2 lg:col-start-2"
                    >
                        <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-[2.5rem] blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="relative aspect-[14/12] max-h-[650px] w-full rounded-[2rem] overflow-hidden border border-border bg-card shadow-2xl hover-glow">
                            <video
                                src="/intro-vid.mp4"
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />

                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                                className="absolute bottom-8 left-8 right-8 bg-background/60 backdrop-blur-xl border border-border p-6 rounded-2xl shadow-xl z-30"
                            >
                                <h3 className="text-lg font-bold font-heading text-white">Driving Digital Transformation</h3>
                                <p className="text-xs text-muted-foreground mt-1 font-body text-slate-300">Accelerating growth with engineering elite.</p>
                            </motion.div>
                        </div>

                        <div className="absolute -top-6 -right-6 w-24 h-24 bg-primary/10 rounded-full blur-xl animate-pulse" />
                        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-xl animate-pulse delay-700" />
                    </motion.div>

                    {/* Left Column Badges - Hidden on mobile, visible on desktop */}
                    <motion.div
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        className="hidden lg:flex items-center gap-6 opacity-60 order-3 lg:col-start-1 lg:mt-[-40px]"
                    >
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                            <ShieldCheck className="w-4 h-4 text-primary" />
                            <span>Enterprise Grade</span>
                        </div>
                        <div className="w-px h-4 bg-border" />
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                            <Sparkles className="w-4 h-4 text-accent" />
                            <span>Future Ready</span>
                        </div>
                    </motion.div>

                </div>
            </div>

            {/* Mobile Scroll Indicator - Below video content */}
            <div className="relative z-30 w-full flex justify-center items-center py-4 mt-4 lg:hidden">
                <div className="flex flex-col items-center gap-2">
                    <span className="text-[10px] tracking-[0.3em] text-foreground/50 uppercase font-black">Scroll</span>
                    <div className="w-px h-8 bg-foreground/30 relative overflow-hidden rounded-full">
                        <motion.div
                            className="absolute top-0 left-0 w-full h-full bg-primary"
                            animate={{ y: [0, 32] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                        />
                    </div>
                </div>
            </div>

            {/* Desktop Scroll Indicator - Unchanged */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none hidden lg:flex"
            >
                <span className="text-[10px] tracking-[0.3em] text-foreground/40 uppercase font-black">Scroll</span>
                <div className="w-px h-12 bg-border relative overflow-hidden">
                    <motion.div
                        className="absolute top-0 left-0 w-full h-1/2 bg-primary"
                        animate={{ y: [0, 48] }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    />
                </div>
            </motion.div>
        </div>
    );
};

export default HeroSection;